import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { sentryService } from '@web-client/services/sentry';
import { addNotification } from '@web-client/state/slices/notifications';
import Environment from '@web-client/services/environment';

interface UseErrorHandlerReturn {
  // Error state
  error: Error | null;
  hasError: boolean;
  errorId: string | null;

  // Error handling methods
  handleError: (error: Error | string, context?: Record<string, unknown>) => void;
  handleAsyncError: (error: Error | string, context?: Record<string, unknown>) => void;
  clearError: () => void;

  // Message capture
  captureMessage: (message: string, level?: 'info' | 'warning' | 'error') => void;
}

export function useErrorHandler(): UseErrorHandlerReturn {
  const [error, setError] = useState<Error | null>(null);
  const [hasError, setHasError] = useState(false);
  const [errorId, setErrorId] = useState<string | null>(null);
  const dispatch = useDispatch();

  const handleError = useCallback((error: Error | string, context?: Record<string, unknown>) => {
    const errorObj = error instanceof Error ? error : new Error(error);
    const id = uuidv4();

    // Prevent infinite loops by checking if we're already handling an error
    if (hasError) {
      console.warn('Already handling an error, skipping to prevent infinite loop');
      return;
    }

    // Set local error state
    setError(errorObj);
    setHasError(true);
    setErrorId(id);

    // Capture in Sentry
    sentryService.captureError(errorObj, {
      ...context,
      errorId: id,
      hook: 'useErrorHandler',
    });

    // Show notification via Redux
    dispatch(addNotification({
      message: errorObj.message || 'An error occurred',
      type: 'error',
    }));

    // Log to console in development
    if (Environment.getInstance().isDev()) {
      // REMOVED: console.error to prevent potential issues
      // console.error('Error handled by useErrorHandler:', errorObj, context);
    }
  }, [dispatch, hasError]);

  const handleAsyncError = useCallback((error: Error | string, context?: Record<string, unknown>) => {
    handleError(error, context);
  }, [handleError]);

  const clearError = useCallback(() => {
    setError(null);
    setHasError(false);
    setErrorId(null);
  }, []);

  const captureMessage = useCallback((message: string, level: 'info' | 'warning' | 'error' = 'info') => {
    // Capture in Sentry
    sentryService.captureMessage(message, level);

    // Show notification via Redux for warnings and errors
    if (level === 'warning' || level === 'error') {
      dispatch(addNotification({
        message,
        type: level,
      }));
    }

    // Log to console in development
    if (Environment.getInstance().isDev()) {
      const logMethod = level === 'error' ? console.error : level === 'warning' ? console.warn : console.log;
      logMethod(`[${level.toUpperCase()}] ${message}`);
    }
  }, [dispatch]);

  return {
    error,
    hasError,
    errorId,
    handleError,
    handleAsyncError,
    clearError,
    captureMessage,
  };
}
